/* @flow */
import * as React from "react";
import UserAgent from "nx-components/UserAgent";
import QueryParams from "nx-components/QueryParams";
import sleep from "nx-components/utils/sleep";

const oldWebView =
  UserAgent.isCrossWalk() || QueryParams.platform() === "ios-uiwebview";

const ANIMATION_DURATION = 200;

// boxShadow makes the pages look like they're on top of each other, but on some
// retina displays it replaces the bottom pixel. Shift it up 10px; the illusion
// of depth will still be there but the bottom pixel is now visible.
const boxShadow = "0 -10px 4px rgba(0, 0, 0, 0.25)";

type Props = {
  children?: React.Node,
  style?: Object,
  initialInValue?: boolean,
  id?: string, // Just for readability
};

// This renders a div with some ref methods on it that change the styles so that
// it transitions in and out. The styles on the div are not updated using
// setState as a performance optimization because re-rendering the entire child
// Page you put inside this as you move it around is unnecessary.
export default class PageTransition extends React.Component<Props> {
  _isIn: boolean =
    this.props.initialInValue != null ? this.props.initialInValue : true;
  _shouldTransition: boolean = true;
  _div: ?HTMLDivElement;

  _slide = async (isIn: boolean) => {
    await sleep(0);
    this._shouldTransition = false;
    this._isIn = !isIn;
    this._updateDivStyle();
    await sleep(0);
    this._shouldTransition = true;
    this._updateDivStyle();
    await sleep(0);
    this._isIn = isIn;
    this._updateDivStyle();
    await this._transitionEndPromise();
  };

  _transitionEndPromise(): Promise<void> {
    if (oldWebView) {
      return Promise.resolve();
    }

    let done;
    return new Promise((resolve) => {
      if (this._div == null) {
        resolve();
        return;
      }
      done = () => {
        if (this._div == null) {
          resolve();
          return;
        }
        this._div.removeEventListener("transitionend", done);
        resolve();
      };
      this._div.addEventListener("transitionend", done);

      // In case the browser doesn't support transitionend, or in case we miss
      // the transitionend (due to the main thread being blocked for the
      // duration of the transition). If this never resolves, the whole app
      // could get frozen (since we await the page transition promise a lot), so
      // we want to have a safe fallback here.
      sleep(ANIMATION_DURATION).then(done);
    });
  }

  /* public ref interface */
  slideIn = () => {
    return this._slide(true);
  };

  slideOut = () => {
    return this._slide(false);
  };

  teleportIn = async () => {
    await sleep(0);
    this._shouldTransition = false;
    this._isIn = true;
    this._updateDivStyle();
    await sleep(0);
  };

  focus = () => {
    if (this._div == null) {
      return;
    }
    this._div.focus();
  };
  /* public ref interface end */

  _calculateDivStylesForTransition() {
    return {
      transition:
        this._shouldTransition && !oldWebView
          ? `transform ${ANIMATION_DURATION}ms ease-in-out`
          : "",
      // Add 2% is to get the shadow off the screen AND work around #157490429
      transform: `translateX(${this._isIn ? "0%" : "102%"}`,
    };
  }

  _updateDivStyle() {
    if (this._div == null) {
      return;
    }

    Object.assign(this._div.style, this._calculateDivStylesForTransition());
  }

  render() {
    const { children, id } = this.props;

    return (
      <div
        id={id}
        ref={(el) => (this._div = el)}
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          boxShadow,
          backgroundColor: "white",
          ...this._calculateDivStylesForTransition(),
          ...this.props.style,
        }}
        tabIndex="-1"
      >
        {children}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/Routing/PageTransition.jsx